import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';

import { useIntl } from 'ha/i18n';
import { Typography, Grid, units } from '@ha/dsl';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';

import TypeFilter from 'ha/components/Filters/TypeFilter';
import FurnitureFilter from 'ha/components/Filters/FurnitureFilter';
import SuitableForFilter from 'ha/components/Filters/SuitableForFilter';
import GenderFilter from 'ha/components/Filters/GenderFilter';
import RegistrationFilter from 'ha/components/Filters/RegistrationFilter';
import SearchAreaFilter from 'ha/components/Filters/SearchAreaFilter';
import LocationFilter from 'ha/components/Filters/LocationFilter';
import PropertySizeFilter from 'ha/components/Filters/PropertySizeFilter';
import ContractTypeFilter from 'ha/components/Filters/ContractTypeFilter';
import PriceAndBillsFilter from 'ha/components/Filters/PriceAndBillsFilter';
import { SearchFiltersContext } from 'ha/pages/Search/SearchFiltersContext';
import FacilitiesFilter from 'ha/components/Filters/FacilitiesFilter';
import AmenitiesFilter from 'ha/components/Filters/AmenitiesFilter';
import { SearchAreaValue } from 'ha/types/SearchFilters';
import { useSearchBarContext } from 'ha/modules/SearchBar/SearchBarContext';

import {
  getIsContractTypeFilterHidden,
  getIsFurnitureFilterHidden,
  getIsGenderFilterHidden,
  getIsRegistrationFilterHidden,
  getIsSuitableForFilterHidden,
  getIsSearchAreaFilterHidden,
  getPropertySizeStats,
  getIsFacilitiesFilterHidden,
  getIsAmenitiesFilterHidden,
} from '../../selectors/filterSelectors';

import SearchFiltersTooltip from './SearchFiltersTooltip';
import SearchDateRangePicker from './SearchDateRangePicker';
import SearchFilterAccordion from './SearchFilterAccordion';
import { getCountryCode } from '../../selectors';

const useStyles = makeStyles(() => ({
  tooltipTitle: {
    marginBottom: units.rem(0.25),
  },
  tooltipParagraph: {
    marginBottom: units.rem(1),
  },
}));

interface OwnProps {
  withDateFilter?: boolean;
}

const SearchFilters: React.FC<OwnProps> = ({ withDateFilter }) => {
  const { T } = useIntl();
  const classes = useStyles();

  const { searchContractTypeFilter, searchLocationFilters } = useFeatureFlags();
  const isLocationEnabled = searchLocationFilters === 'on';

  const isContractTypeFilterHidden =
    useSelector(getIsContractTypeFilterHidden) ||
    searchContractTypeFilter !== 'on';
  const isFurnitureFilterHidden = useSelector(getIsFurnitureFilterHidden);
  const isGenderFilterHidden = useSelector(getIsGenderFilterHidden);
  const isRegistrationFilterHidden = useSelector(getIsRegistrationFilterHidden);
  const isSuitableForFilterHidden = useSelector(getIsSuitableForFilterHidden);
  const isSearchAreaFilterHidden = useSelector(getIsSearchAreaFilterHidden);
  const isFacilitiesFilterHidden = useSelector(getIsFacilitiesFilterHidden);
  const isAmenitiesFilterHidden = useSelector(getIsAmenitiesFilterHidden);

  const countryCode = useSelector(getCountryCode);
  const isSpain = countryCode === 'es';
  const isItaly = countryCode === 'it';

  const propertySizeStats = useSelector(getPropertySizeStats);

  const {
    localState,
    filtersCount,
    priceParams,
    onChangeDates,
    onChangeContractType,
    onChangeRooms,
    onChangeTypes,
    onChangePrice,
    onChangeCurrency,
    onChangeFurniture,
    onChangeBills,
    onChangeSuitableFor,
    onChangeGender,
    onChangeRegistration,
    onChangeSearchAreaFilter,
    onChangeRadius,
    onChangePropertySize,
    onChangeFacilities,
    onChangeAmenities,
    onChangePlace,
  } = React.useContext(SearchFiltersContext);
  const { initialSuggestions } = useSearchBarContext();

  return (
    <React.Fragment>
      {isLocationEnabled && (
        <SearchFilterAccordion
          label={T('search.filters.location.title')}
          defaultExpanded
          dataTestLocator="Search Area"
        >
          <LocationFilter
            placeProps={{
              value: localState.place,
              onChange: onChangePlace,
              initialItems: initialSuggestions,
              changeOnlyOnSelect: true,
            }}
            searchAreaProps={{
              value: localState.searchArea,
              onChange: onChangeSearchAreaFilter,
              location: 'filter',
            }}
            radiusProps={{
              value: localState.radius,
              onChange: onChangeRadius,
            }}
          />
        </SearchFilterAccordion>
      )}
      <SearchFilterAccordion
        label={T('Price')}
        defaultExpanded
        dataTestLocator="Price"
      >
        <PriceAndBillsFilter
          price={{
            value: localState.price,
            onChange: onChangePrice,
            currencyValue: localState.price.currency,
            onCurrencyChange: onChangeCurrency,
            ...priceParams,
          }}
          bills={{
            value: localState.bills,
            onChange: onChangeBills,
            count: filtersCount.bills,
          }}
        />
      </SearchFilterAccordion>

      {withDateFilter && (
        <SearchFilterAccordion
          label={T('Date')}
          defaultExpanded
          dataTestLocator="Date"
        >
          <SearchDateRangePicker
            value={localState.dates}
            onChange={onChangeDates}
          />
        </SearchFilterAccordion>
      )}

      <SearchFilterAccordion
        label={T('Property type')}
        defaultExpanded
        dataTestLocator="Property Type"
      >
        <TypeFilter
          value={localState.types}
          onChange={onChangeTypes}
          roomValue={localState.rooms}
          onChangeRooms={onChangeRooms}
          count={filtersCount.propertyType}
          roomsCount={filtersCount.rooms}
        />
      </SearchFilterAccordion>

      <SearchFilterAccordion
        label={T('search.filters.property_size.title')}
        defaultExpanded={Boolean(
          localState.propertySize?.sizeMin || localState.propertySize?.sizeMax,
        )}
        dataTestLocator="Property Size"
      >
        <PropertySizeFilter
          value={localState.propertySize}
          onChange={onChangePropertySize}
          count={filtersCount.propertySize}
          stats={propertySizeStats}
        />
      </SearchFilterAccordion>

      {!isContractTypeFilterHidden && (
        <SearchFilterAccordion
          label={
            <Grid container alignItems="baseline">
              <Grid item>
                <Typography.ParagraphTitle>
                  {T('rental_contract.title')}
                </Typography.ParagraphTitle>
              </Grid>
              <Grid item>
                <SearchFiltersTooltip
                  title={
                    <Typography.H2>{T('rental_contract.title')}</Typography.H2>
                  }
                  text={
                    <React.Fragment>
                      <Typography.Caption
                        fontWeight="bold"
                        className={classes.tooltipTitle}
                      >
                        {T('rental_contract.daily')}
                      </Typography.Caption>
                      <Typography.Caption className={classes.tooltipParagraph}>
                        {T('rental_contract.daily.tooltip')}
                      </Typography.Caption>
                      <Typography.Caption
                        fontWeight="bold"
                        className={classes.tooltipTitle}
                      >
                        {T('rental_contract.fortnightly')}
                      </Typography.Caption>
                      <Typography.Caption className={classes.tooltipParagraph}>
                        {T('rental_contract.fortnightly.tooltip')}
                      </Typography.Caption>
                      <Typography.Caption
                        fontWeight="bold"
                        className={classes.tooltipTitle}
                      >
                        {T('rental_contract.monthly')}
                      </Typography.Caption>
                      <Typography.Caption>
                        {T('rental_contract.monthly.tooltip')}
                      </Typography.Caption>
                    </React.Fragment>
                  }
                />
              </Grid>
            </Grid>
          }
          defaultExpanded={Boolean(localState.contractType.length)}
          dataTestLocator="Contract Type"
        >
          <ContractTypeFilter
            value={localState.contractType}
            onChange={onChangeContractType}
            count={filtersCount.contractType}
          />
        </SearchFilterAccordion>
      )}

      {!isFurnitureFilterHidden && (
        <SearchFilterAccordion
          label={T('Furniture')}
          defaultExpanded={Boolean(localState.furniture.length)}
          dataTestLocator="Furniture"
        >
          <FurnitureFilter
            value={localState.furniture}
            onChange={onChangeFurniture}
            count={filtersCount.furniture}
          />
        </SearchFilterAccordion>
      )}

      {!isFacilitiesFilterHidden && (
        <SearchFilterAccordion
          label={T('Facilities')}
          defaultExpanded={Boolean(localState.facilities.length)}
          dataTestLocator="Facilities Filter"
        >
          <FacilitiesFilter
            value={localState.facilities}
            onChange={onChangeFacilities}
            count={filtersCount.facilities}
          />
        </SearchFilterAccordion>
      )}

      {!isAmenitiesFilterHidden && (
        <SearchFilterAccordion
          label={T('Amenities')}
          defaultExpanded={Boolean(localState.amenities.length)}
          dataTestLocator="Amenities Filter"
        >
          <AmenitiesFilter
            value={localState.amenities}
            onChange={onChangeAmenities}
            count={filtersCount.amenities}
          />
        </SearchFilterAccordion>
      )}

      {(!isSuitableForFilterHidden || !isGenderFilterHidden) && (
        <SearchFilterAccordion
          label={T('Suitable for')}
          defaultExpanded={Boolean(
            localState.suitableFor.length || localState.gender.length,
          )}
          dataTestLocator="Suitable For"
        >
          <Grid container direction="column">
            <Grid item>
              <SuitableForFilter
                value={localState.suitableFor}
                onChange={onChangeSuitableFor}
                count={filtersCount.suitableFor}
              />
            </Grid>
            <Grid item>
              <GenderFilter
                value={localState.gender}
                onChange={onChangeGender}
                count={filtersCount.gender}
              />
            </Grid>
          </Grid>
        </SearchFilterAccordion>
      )}

      {!isSpain && !isItaly && !isRegistrationFilterHidden && (
        <SearchFilterAccordion
          label={T('Registration')}
          defaultExpanded={Boolean(localState.registration.length)}
          dataTestLocator="Registration"
        >
          <RegistrationFilter
            value={localState.registration}
            onChange={onChangeRegistration}
            count={filtersCount.registration}
          />
        </SearchFilterAccordion>
      )}

      {!isLocationEnabled && !isSearchAreaFilterHidden && (
        <SearchFilterAccordion
          label={T('search.map.map_as_filter.title')}
          defaultExpanded={localState.searchArea.includes(
            SearchAreaValue.MapBounds,
          )}
          dataTestLocator="Search Area"
        >
          <SearchAreaFilter
            value={localState.searchArea}
            onChange={onChangeSearchAreaFilter}
            location="filter"
          />
        </SearchFilterAccordion>
      )}
    </React.Fragment>
  );
};

export default SearchFilters;
